/**
 * ImportSection — XLS import workflow for the Export/Data Preview page.
 *
 * Extracted from `pages/ExportDataPage.jsx` (Oct 2 2026) as part of the
 * frontend page-slimming refactor. Behaviour is identical; only the file
 * location changed.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Download,
  Database,
  Loader2,
  FileSpreadsheet,
  X,
  Check,
  Calendar,
  Upload,
  FileUp,
  ArrowRight,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  History,
  RotateCcw,
  Trash2,
  Info,
  Plus,
  Filter,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { authFetch, authJsonFetch } from '../../lib/api';
import { useManagedYears } from '../../hooks/useManagedYears';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

// Tables that have year-based data (per org per year)
const YEAR_BASED_TABLES = ['sales', 'sales_data', 'refunds', 'workflows', 'orders'];

// Helper to format field labels (title-case + underscores → spaces).
const formatFieldLabel = (field) =>
  String(field || '').replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

// Badge colour per schema type.
const getTypeBadgeColor = (type) => {
  switch (type) {
    case 'string': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
    case 'number': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
    case 'boolean': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300';
    case 'date': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300';
    case 'reference': return 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300';
    default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
  }
};

const ImportSection = ({ isBarbiePink }) => {
  const managedYears = useManagedYears();
  const dynamicYearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const base = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];
    const merged = Array.from(new Set([...(managedYears.years || []), ...base]));
    return merged.sort((a, b) => a - b);
  }, [managedYears.years]);

  const [importTables, setImportTables] = useState({});
  const [loadingTables, setLoadingTables] = useState(true);
  const [selectedTable, setSelectedTable] = useState('');
  const [uploading, setUploading] = useState(false);
  const [importSession, setImportSession] = useState(null);
  const [columnMappings, setColumnMappings] = useState([]);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [conflictMode, setConflictMode] = useState('skip');
  const [importHistory, setImportHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [reverting, setReverting] = useState(null);
  
  // Organisation matching
  const [orgMatchColumn, setOrgMatchColumn] = useState('');
  
  // Year selection for year-based tables
  const [importYear, setImportYear] = useState(new Date().getFullYear());
  
  // Column merging - maps target_field to array of source columns
  const [mergedFields, setMergedFields] = useState({}); // { "name": ["First Name", "Last Name"] }
  
  // Step tracking
  const [currentStep, setCurrentStep] = useState(1); // 1: Select & Upload, 2: Map Columns, 3: Validate, 4: Execute
  
  // Notes Import state
  const [notesFile, setNotesFile] = useState(null);
  const [notesUploading, setNotesUploading] = useState(false);
  const [notesImportSession, setNotesImportSession] = useState(null);
  const [notesImporting, setNotesImporting] = useState(false);
  const [notesForceReattribute, setNotesForceReattribute] = useState(false);
  const [notesProgress, setNotesProgress] = useState(null); // { progress, running_results, total_notes }
  
  // Flush-by-year state
  const [flushDialogOpen, setFlushDialogOpen] = useState(false);
  const [flushPreview, setFlushPreview] = useState(null); // { record_count, org_count }
  const [flushPreviewLoading, setFlushPreviewLoading] = useState(false);
  const [flushConfirmInput, setFlushConfirmInput] = useState('');
  const [flushBackupChoice, setFlushBackupChoice] = useState(null); // null | 'yes' | 'no'
  const [flushing, setFlushing] = useState(false);
  const [flushBeforeImport, setFlushBeforeImport] = useState(false); // checkbox for pre-import flush
  const [pendingImportAfterFlush, setPendingImportAfterFlush] = useState(false); // chain flush → import
  
  // Fetch importable tables
  useEffect(() => {
    const fetchTables = async () => {
      try {
        const response = await authFetch(`${API}/import-data/tables`);
        if (response.ok) {
          const data = await response.json();
          setImportTables(data.tables || {});
        }
      } catch (err) {
        console.error('Failed to fetch import tables:', err);
      } finally {
        setLoadingTables(false);
      }
    };
    fetchTables();
  }, []);
  
  // Fetch import history
  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await authFetch(`${API}/import-data/history`);
      if (response.ok) {
        const data = await response.json();
        setImportHistory(data.imports || []);
      }
    } catch (err) {
      console.error('Failed to fetch import history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Flush-by-year: open dialog and pre-load count of records that will be deleted
  const openFlushDialog = async () => {
    if (!selectedTable || !importYear) {
      toast.error('Select a year-based table and year first');
      return;
    }
    setFlushDialogOpen(true);
    setFlushPreview(null);
    setFlushConfirmInput('');
    setFlushBackupChoice(null);
    setFlushPreviewLoading(true);
    try {
      const res = await authFetch(`${API}/import-data/flush-preview/${selectedTable}/${importYear}`);
      if (res.ok) {
        setFlushPreview(await res.json());
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Failed to load flush preview');
        setFlushDialogOpen(false);
      }
    } catch (err) {
      console.error('Flush preview failed:', err);
      toast.error('Failed to load flush preview');
      setFlushDialogOpen(false);
    } finally {
      setFlushPreviewLoading(false);
    }
  };

  // Run an admin database-export (xlsx backup), then return when ready
  const runBackupBeforeFlush = async () => {
    try {
      const res = await authFetch(`${API}/database/export?type=full`);
      if (!res.ok) throw new Error('Backup failed');
      const blob = await res.blob();
      // Trigger download in browser
      const cd = res.headers.get('content-disposition') || '';
      const match = cd.match(/filename="?([^";]+)"?/);
      const filename = match ? match[1] : `gs_workflow_backup_${Date.now()}.xlsx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Backup downloaded: ${filename}`);
      return true;
    } catch (err) {
      console.error('Backup failed:', err);
      toast.error('Backup failed — flush aborted');
      return false;
    }
  };

  // Execute flush (with optional backup-first prompt resolved)
  const executeFlush = async () => {
    if (!flushPreview) return;
    if (flushConfirmInput !== String(importYear)) {
      toast.error(`Type ${importYear} to confirm`);
      return;
    }
    setFlushing(true);
    try {
      // 1) Optional backup
      if (flushBackupChoice === 'yes') {
        const ok = await runBackupBeforeFlush();
        if (!ok) return; // bail — don't flush if backup failed
      }
      // 2) Flush
      const res = await authFetch(`${API}/import-data/flush-year`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          table: selectedTable,
          year: importYear,
          confirmation: String(importYear),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Flush failed');
        return;
      }
      toast.success(`Flushed ${data.deleted_count} records from ${selectedTable} for year ${importYear}`);
      setFlushDialogOpen(false);
      // Refresh history so the flush audit entry shows up
      fetchHistory();

      // If this flush was triggered by the "flush before import" checkbox, chain
      // straight into the import now that the year is empty.
      if (pendingImportAfterFlush) {
        setPendingImportAfterFlush(false);
        setFlushBeforeImport(false); // one-shot
        // Re-trigger import — the guard above sees flushBeforeImport=false and proceeds
        setTimeout(() => handleExecute(), 200);
      }
    } catch (err) {
      console.error('Flush failed:', err);
      toast.error('Flush failed');
    } finally {
      setFlushing(false);
    }
  };

  
  // Check if table needs org reference
  const needsOrgReference = selectedTable && selectedTable !== 'organizations' && 
    importTables[selectedTable]?.reference_fields?.org_id;
  
  // Check if table is year-based
  const isYearBasedTable = selectedTable && (
    YEAR_BASED_TABLES.includes(selectedTable) || 
    importTables[selectedTable]?.year_based
  );
  
  // Handle file upload using XMLHttpRequest (more reliable for file uploads)
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!selectedTable) {
      toast.error('Please select a table first');
      return;
    }
    
    if (isYearBasedTable && !importYear) {
      toast.error('Please select a year for this data');
      return;
    }
    
    setUploading(true);
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('table', selectedTable);
    if (isYearBasedTable) {
      formData.append('year', importYear.toString());
    }
    
    console.log('Uploading file:', file.name, 'Size:', file.size, 'Table:', selectedTable, 'Year:', isYearBasedTable ? importYear : 'N/A');
    
    // Use XMLHttpRequest for better file upload handling
    const xhr = new XMLHttpRequest();
    
    xhr.onload = function() {
      console.log('Upload response status:', xhr.status);
      console.log('Upload response text:', xhr.responseText?.substring(0, 500));
      
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          setImportSession(data);
          
          // Initialize column mappings
          const initialMappings = data.columns.map(col => ({
            source_column: col,
            target_field: '',
            is_reference: false,
            reference_value: ''
          }));
          setColumnMappings(initialMappings);
          setCurrentStep(2);
          toast.success(`File uploaded: ${data.row_count} rows detected`);
        } catch (parseErr) {
          console.error('Failed to parse response:', parseErr);
          toast.error('Invalid response from server');
        }
      } else {
        let errorDetail = `Upload failed (${xhr.status})`;
        try {
          const errJson = JSON.parse(xhr.responseText);
          errorDetail = errJson.detail || errorDetail;
        } catch {
          if (xhr.status === 403) {
            errorDetail = 'Admin access required';
          } else if (xhr.status === 413) {
            errorDetail = 'File too large';
          } else if (xhr.responseText) {
            errorDetail = xhr.responseText.substring(0, 200);
          }
        }
        console.error('Upload error:', errorDetail);
        toast.error(errorDetail);
      }
      setUploading(false);
      e.target.value = '';
    };
    
    xhr.onerror = function() {
      console.error('Upload network error');
      toast.error('Network error - please check your connection');
      setUploading(false);
      e.target.value = '';
    };
    
    xhr.ontimeout = function() {
      console.error('Upload timeout');
      toast.error('Upload timed out - file may be too large');
      setUploading(false);
      e.target.value = '';
    };
    
    // Get auth token
    const token = localStorage.getItem('gs_session_token');
    
    xhr.open('POST', `${API}/import-data/upload`);
    xhr.timeout = 120000; // 2 minute timeout
    xhr.withCredentials = true;
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }
    xhr.send(formData);
  };
  
  // Update column mapping
  const updateMapping = (sourceColumn, field, value) => {
    setColumnMappings(prev => prev.map(m => 
      m.source_column === sourceColumn 
        ? { ...m, [field]: value }
        : m
    ));
  };
  
  // Save mappings and validate
  const handleValidate = async () => {
    if (!importSession) return;
    
    // Filter out unmapped columns and skipped columns
    const validMappings = columnMappings.filter(m => m.target_field && m.target_field !== '__skip__');
    
    // Check if org matching is needed but not configured
    if (needsOrgReference && !orgMatchColumn) {
      toast.error('Please select the column that contains Organisation Names');
      return;
    }
    
    if (validMappings.length === 0 && Object.keys(mergedFields).length === 0) {
      toast.error('Please map at least one column');
      return;
    }
    
    setValidating(true);
    try {
      // Prepare mapping data with org matching and merged fields
      // Filter out __temp__ key from mergedFields
      const cleanedMergedFields = {};
      Object.entries(mergedFields).forEach(([key, value]) => {
        if (!key.startsWith('__') && Array.isArray(value) && value.length > 0) {
          cleanedMergedFields[key] = value;
        }
      });
      
      const mappingData = {
        mappings: validMappings,
        org_match_column: needsOrgReference ? orgMatchColumn : null,
        merged_fields: cleanedMergedFields
      };
      
      // Save mappings first
      await authJsonFetch(`${API}/import-data/${importSession.import_id}/mapping`, {
        method: 'POST',
        body: JSON.stringify(mappingData)
      });
      
      // Then validate
      const response = await authFetch(`${API}/import-data/${importSession.import_id}/validate`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        setValidationResult(data);
        setCurrentStep(3);
        
        if (data.invalid_rows === 0) {
          toast.success(`Validation passed: ${data.valid_rows} valid rows`);
        } else {
          toast.warning(`Validation complete: ${data.invalid_rows} rows have errors`);
        }
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Validation failed');
      }
    } catch (err) {
      toast.error('Validation failed');
    } finally {
      setValidating(false);
    }
  };
  
  // Execute import (background task with polling)
  const handleExecute = async () => {
    if (!importSession) return;
    
    // Pre-import flush: if user ticked "Flush before import" for a year-based table,
    // open the same confirmation dialog they'd see from the standalone Flush button.
    // After they confirm + (optionally) backup, the dialog calls back into executeImport.
    if (flushBeforeImport && isYearBasedTable && !flushDialogOpen) {
      // Re-use the standalone flush dialog. We add a one-shot continuation so the
      // import auto-runs after the flush succeeds.
      setPendingImportAfterFlush(true);
      await openFlushDialog();
      return;
    }
    
    setExecuting(true);
    try {
      // Start the import (returns immediately)
      const response = await authJsonFetch(
        `${API}/import-data/${importSession.import_id}/execute`,
        { 
          method: 'POST',
          body: JSON.stringify({ conflict_mode: conflictMode })
        }
      );
      
      if (!response.ok) {
        const err = await response.json();
        toast.error(err.detail || 'Import failed to start');
        setExecuting(false);
        return;
      }
      
      toast.info('Import started... processing rows in background');
      
      // Poll for completion
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await authFetch(
            `${API}/import-data/${importSession.import_id}/execute-status`
          );
          if (statusRes.ok) {
            const status = await statusRes.json();
            
            if (status.status === 'completed') {
              clearInterval(pollInterval);
              const data = status.results || {};
              const successMsg = `Import complete: ${data.inserted || 0} inserted, ${data.updated || 0} updated, ${data.skipped || 0} skipped`;
              if (data.errors?.length > 0) {
                toast.warning(`${successMsg} (${data.errors.length} errors)`);
              } else {
                toast.success(successMsg);
              }
              setCurrentStep(4);
              setExecuting(false);
              setTimeout(() => { resetImport(); }, 3000);
            } else if (status.status === 'error') {
              clearInterval(pollInterval);
              toast.error(`Import failed: ${status.error || 'Unknown error'}`);
              setExecuting(false);
            }
            // Still running - continue polling
          }
        } catch (pollErr) {
          // Polling error - keep trying
          console.warn('Import status poll error:', pollErr);
        }
      }, 2000); // Poll every 2 seconds
      
      // Safety timeout - stop polling after 10 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        if (executing) {
          toast.warning('Import is still running in the background. Check import history for results.');
          setExecuting(false);
        }
      }, 600000);
      
    } catch (err) {
      console.error('Import execute error:', err);
      toast.error('Import execution failed');
      setExecuting(false);
    }
  };
  
  // Revert import
  const handleRevert = async (importId) => {
    setReverting(importId);
    try {
      const response = await authFetch(`${API}/import-data/${importId}/revert`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchHistory();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Revert failed');
      }
    } catch (err) {
      toast.error('Revert failed');
    } finally {
      setReverting(null);
    }
  };
  
  // Reset import state
  const resetImport = () => {
    setImportSession(null);
    setColumnMappings([]);
    setValidationResult(null);
    setCurrentStep(1);
    setConflictMode('skip');
    setOrgMatchColumn('');
    setMergedFields({});
  };
  
  // ==================== Notes Import Functions ====================
  
  const handleNotesFileSelect = (e) => {
    const fileList = Array.from(e.target.files || []);
    if (fileList.length > 0) {
      setNotesFile(fileList);
      setNotesImportSession(null);
    }
  };
  
  const handleNotesUpload = async () => {
    const fileArr = Array.isArray(notesFile) ? notesFile : (notesFile ? [notesFile] : []);
    if (!fileArr.length) return;
    
    setNotesUploading(true);
    try {
      const formData = new FormData();
      // FastAPI expects multiple files under the SAME field name 'files'
      fileArr.forEach(f => formData.append('files', f));
      
      const response = await fetch(`${API}/import-data/notes/upload`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setNotesImportSession(data);
        const fileCountLabel = fileArr.length === 1 ? '1 file' : `${fileArr.length} files`;
        toast.success(`Parsed ${data.total_notes} notes from ${data.organizations_found?.length || 0} organizations across ${fileCountLabel}`);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to parse notes file');
      }
    } catch (err) {
      console.error('Notes upload error:', err);
      toast.error('Failed to upload notes file');
    } finally {
      setNotesUploading(false);
    }
  };
  
  const handleNotesImport = async () => {
    if (!notesImportSession?.import_id) return;
    
    setNotesImporting(true);
    setNotesProgress({
      progress: 'Queued…',
      running_results: {},
      total_notes: notesImportSession.total_notes,
    });
    try {
      const response = await authJsonFetch(`${API}/import-data/notes/${notesImportSession.import_id}/execute?force_reattribute=${notesForceReattribute ? 'true' : 'false'}`, {
        method: 'POST'
      });
      
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to import notes');
        setNotesImporting(false);
        setNotesProgress(null);
        return;
      }

      const enqueueData = await response.json();

      // If server returned final results inline (legacy synchronous path), accept it.
      if (enqueueData.imported !== undefined || enqueueData.results) {
        toast.success(enqueueData.message || 'Notes imported');
        setNotesImportSession(prev => ({ ...prev, results: enqueueData.results || enqueueData, status: 'completed' }));
        setNotesImporting(false);
        setNotesProgress(null);
        return;
      }

      const importId = notesImportSession.import_id;
      let attempts = 0;
      const MAX_ATTEMPTS = 720; // 720 * 3s = 36 min
      const poll = async () => {
        attempts += 1;
        try {
          const r = await authJsonFetch(`${API}/import-data/notes/${importId}/execute-status`);
          if (!r.ok) {
            toast.error('Lost connection to notes import status');
            setNotesImporting(false);
            setNotesProgress(null);
            return;
          }
          const status = await r.json();

          // Update live progress display
          setNotesProgress(prev => ({
            progress: status.execute_progress || prev?.progress || 'Working…',
            running_results: status.execute_running_results || prev?.running_results || {},
            total_notes: notesImportSession.total_notes,
          }));

          if (status.status === 'completed') {
            const results = status.results || {};
            const msg = `Import complete: ${results.imported || 0} imported, ${results.duplicates || 0} duplicates, ${results.contacts_created || 0} contacts`;
            toast.success(msg);
            setNotesImportSession(prev => ({ ...prev, results, status: 'completed' }));
            setNotesImporting(false);
            setNotesProgress(null);
            return;
          }
          if (status.status === 'failed') {
            toast.error(`Notes import failed: ${status.execute_error || status.execute_progress || 'Unknown error'}`);
            setNotesImporting(false);
            setNotesProgress(null);
            return;
          }
          if (attempts >= MAX_ATTEMPTS) {
            toast.error('Notes import is taking longer than expected. Refresh later to see status.');
            setNotesImporting(false);
            setNotesProgress(null);
            return;
          }
          setTimeout(poll, 3000); // Poll every 3s for snappier updates
        } catch (e) {
          console.error('Notes import poll error:', e);
          if (attempts < MAX_ATTEMPTS) {
            setTimeout(poll, 3000);
          } else {
            setNotesImporting(false);
            setNotesProgress(null);
          }
        }
      };
      setTimeout(poll, 1500);
    } catch (err) {
      console.error('Notes import error:', err);
      toast.error('Notes import failed');
      setNotesImporting(false);
      setNotesProgress(null);
    }
  };
  
  const resetNotesImport = () => {
    setNotesFile(null);
    setNotesImportSession(null);
  };
  
  // Add/remove column from merged field
  const toggleMergedColumn = (targetField, sourceColumn) => {
    setMergedFields(prev => {
      const current = prev[targetField] || [];
      if (current.includes(sourceColumn)) {
        // Remove column
        const updated = current.filter(c => c !== sourceColumn);
        if (updated.length === 0) {
          const { [targetField]: _, ...rest } = prev;
          return rest;
        }
        return { ...prev, [targetField]: updated };
      } else {
        // Add column
        return { ...prev, [targetField]: [...current, sourceColumn] };
      }
    });
  };
  
  // Get available target fields for mapping
  const getTargetFields = () => {
    if (!selectedTable || !importTables[selectedTable]) return [];
    return Object.entries(importTables[selectedTable].fields || {}).map(([key, value]) => ({
      key,
      label: value.label || formatFieldLabel(key),
      type: value.type,
      required: value.required
    }));
  };
  
  if (loadingTables) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Step Indicator */}
      <div className="flex items-center justify-between px-4">
        {[
          { step: 1, label: 'Upload File' },
          { step: 2, label: 'Map Columns' },
          { step: 3, label: 'Validate' },
          { step: 4, label: 'Complete' }
        ].map((s, idx) => (
          <React.Fragment key={s.step}>
            <div className="flex items-center gap-2">
              <div className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors",
                currentStep >= s.step 
                  ? isBarbiePink 
                    ? "bg-pink-500 text-white" 
                    : "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground"
              )}>
                {currentStep > s.step ? <Check className="w-4 h-4" /> : s.step}
              </div>
              <span className={cn(
                "text-sm hidden sm:block",
                currentStep >= s.step ? "text-foreground font-medium" : "text-muted-foreground"
              )}>
                {s.label}
              </span>
            </div>
            {idx < 3 && (
              <div className={cn(
                "flex-1 h-0.5 mx-2",
                currentStep > s.step ? (isBarbiePink ? "bg-pink-500" : "bg-primary") : "bg-muted"
              )} />
            )}
          </React.Fragment>
        ))}
      </div>
      
      {/* Step 1: Select Table & Upload */}
      {currentStep === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Select Table & Upload File
            </CardTitle>
            <CardDescription>
              Choose a table to import data into, then upload your Excel or CSV file
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Table Selection */}
            <div className="space-y-2">
              <Label>Target Table</Label>
              <Select value={selectedTable} onValueChange={setSelectedTable}>
                <SelectTrigger className="w-full" data-testid="import-table-select">
                  <SelectValue placeholder="Select a table to import into..." />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(importTables).map(([key, table]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <Database className="w-4 h-4" />
                        {table.label}
                        {table.year_based && (
                          <Badge variant="outline" className="text-[10px] ml-2">per year</Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Table Info */}
            {selectedTable && importTables[selectedTable] && (
              <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Info className="w-4 h-4 text-blue-500" />
                  <span className="font-medium">Table Information</span>
                </div>
                <div className="text-sm text-muted-foreground">
                  <p><strong>Key Fields:</strong> {importTables[selectedTable].key_fields?.join(', ') || 'None'}</p>
                  {importTables[selectedTable].year_based && (
                    <p className="mt-1"><strong>Note:</strong> This table requires year and organisation for each record</p>
                  )}
                  {Object.keys(importTables[selectedTable].reference_fields || {}).length > 0 && (
                    <p className="mt-1"><strong>References:</strong> {Object.keys(importTables[selectedTable].reference_fields).join(', ')}</p>
                  )}
                </div>
              </div>
            )}
            
            {/* Year Selection for year-based tables */}
            {isYearBasedTable && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Import Year <span className="text-destructive">*</span>
                </Label>
                <div className="flex flex-wrap items-center gap-2">
                  <Select value={importYear.toString()} onValueChange={(v) => setImportYear(parseInt(v))}>
                    <SelectTrigger className="w-48" data-testid="import-year-select">
                      <SelectValue placeholder="Select year..." />
                    </SelectTrigger>
                    <SelectContent>
                      {dynamicYearOptions.map((year) => (
                        <SelectItem key={year} value={year.toString()}>
                          {year}
                        </SelectItem>
                      ))}
                      {managedYears.isAdmin && (
                        <div
                          role="button"
                          tabIndex={0}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const input = window.prompt('Add a new year:', String(new Date().getFullYear() + 1));
                            if (!input) return;
                            managedYears.addYear(parseInt(input, 10));
                          }}
                          className="px-2 py-1.5 text-xs text-primary hover:bg-muted cursor-pointer border-t mt-1 pt-1.5"
                          data-testid="import-year-add"
                        >
                          + Add year…
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={openFlushDialog}
                    className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                    data-testid="flush-year-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Flush {importYear} Data
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  All imported records will be assigned to year <strong>{importYear}</strong>.
                  Flush deletes all existing rows for {importYear} across every organisation in this table.
                </p>
                <div className="flex items-start gap-2 pt-2">
                  <input
                    type="checkbox"
                    id="flush-before-import"
                    checked={flushBeforeImport}
                    onChange={(e) => setFlushBeforeImport(e.target.checked)}
                    className="mt-1"
                    data-testid="flush-before-import-checkbox"
                  />
                  <Label htmlFor="flush-before-import" className="text-xs font-normal cursor-pointer leading-snug">
                    Flush all existing <strong>{importYear}</strong> records in this table before running the import
                    (you'll be asked to confirm + offered a backup just before the import executes)
                  </Label>
                </div>
              </div>
            )}
            
            {/* File Upload */}
            <div className="space-y-2">
              <Label>Upload File</Label>
              <div className={cn(
                "border-2 border-dashed rounded-lg p-8 text-center transition-colors",
                selectedTable 
                  ? "border-muted-foreground/25 hover:border-primary cursor-pointer" 
                  : "border-muted-foreground/10 cursor-not-allowed opacity-50"
              )}>
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileUpload}
                  disabled={!selectedTable || uploading}
                  className="hidden"
                  id="import-file-input"
                  data-testid="import-file-input"
                />
                <label htmlFor="import-file-input" className={cn("cursor-pointer", !selectedTable && "pointer-events-none")}>
                  {uploading ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="w-10 h-10 animate-spin text-muted-foreground" />
                      <span className="text-muted-foreground">Uploading...</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <FileUp className="w-10 h-10 text-muted-foreground" />
                      <span className="text-muted-foreground">
                        {selectedTable 
                          ? 'Click to upload or drag and drop'
                          : 'Select a table first'}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Supports .xlsx, .xls, .csv files
                      </span>
                    </div>
                  )}
                </label>
              </div>
            </div>
            
            {/* History Button */}
            <div className="flex justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  fetchHistory();
                  setShowHistoryDialog(true);
                }}
                data-testid="import-history-btn"
              >
                <History className="w-4 h-4 mr-2" />
                Import History
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Step 2: Column Mapping */}
      {currentStep === 2 && importSession && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <ArrowRight className="w-5 h-5" />
                  Map Columns
                </CardTitle>
                <CardDescription>
                  Match your file columns to database fields
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={resetImport}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* File Info */}
            <div className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg text-sm">
              <FileSpreadsheet className="w-5 h-5 text-green-500" />
              <span><strong>{importSession.filename}</strong></span>
              <Badge variant="outline">{importSession.row_count} rows</Badge>
              <Badge variant="secondary">{importSession.columns.length} columns</Badge>
              {importSession.year && (
                <Badge variant="default" className="bg-blue-500">
                  <Calendar className="w-3 h-3 mr-1" />
                  Year: {importSession.year}
                </Badge>
              )}
            </div>
            
            {/* Organisation Matching Section - only for tables that need org reference */}
            {needsOrgReference && (
              <div className="p-4 border-2 border-dashed border-orange-300 dark:border-orange-700 rounded-lg bg-orange-50 dark:bg-orange-950/20 space-y-3">
                <div className="flex items-center gap-2">
                  <Database className="w-5 h-5 text-orange-500" />
                  <Label className="font-semibold text-orange-700 dark:text-orange-400">
                    Organisation Matching (Required)
                  </Label>
                </div>
                <p className="text-sm text-muted-foreground">
                  Select the column in your file that contains Organisation Names. 
                  The system will match these to existing organisations in the database.
                </p>
                <Select value={orgMatchColumn} onValueChange={setOrgMatchColumn}>
                  <SelectTrigger className="w-full" data-testid="org-match-column">
                    <SelectValue placeholder="Select the Organisation Name column..." />
                  </SelectTrigger>
                  <SelectContent>
                    {importSession.columns.map(col => (
                      <SelectItem key={col} value={col}>
                        {col}
                        <span className="text-xs text-muted-foreground ml-2">
                          (e.g., "{importSession.sample_rows?.[0]?.[col] || 'empty'}")
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {orgMatchColumn && (
                  <div className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                    <Check className="w-3 h-3" />
                    Will match "{orgMatchColumn}" column to existing organisations
                  </div>
                )}
              </div>
            )}
            
            {/* Column Merging Section */}
            <div className="p-4 border rounded-lg bg-blue-50 dark:bg-blue-950/20 space-y-3">
              <div className="flex items-center gap-2">
                <ArrowRight className="w-5 h-5 text-blue-500" />
                <Label className="font-semibold text-blue-700 dark:text-blue-400">
                  Merge Columns (Optional)
                </Label>
              </div>
              <p className="text-sm text-muted-foreground">
                Combine multiple columns into one field (e.g., "First Name" + "Last Name" → "Name")
              </p>
              
              {/* Active merged fields */}
              {Object.entries(mergedFields).length > 0 && (
                <div className="space-y-2">
                  {Object.entries(mergedFields).map(([targetField, columns]) => (
                    <div key={targetField} className="flex items-center gap-2 p-2 bg-white dark:bg-gray-800 rounded border">
                      <Badge variant="secondary">{columns.join(' + ')}</Badge>
                      <ArrowRight className="w-4 h-4" />
                      <Badge>{getTargetFields().find(f => f.key === targetField)?.label || targetField}</Badge>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setMergedFields(prev => {
                          const { [targetField]: _, ...rest } = prev;
                          return rest;
                        })}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add new merge */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs mb-1 block">Source Columns (select multiple)</Label>
                  <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto p-2 border rounded bg-white dark:bg-gray-800">
                    {importSession.columns.map(col => {
                      const isInMerge = Object.values(mergedFields).flat().includes(col);
                      const isOrgMatch = col === orgMatchColumn;
                      return (
                        <Badge 
                          key={col}
                          variant={isInMerge ? "default" : "outline"}
                          className={cn(
                            "cursor-pointer text-xs",
                            isOrgMatch && "opacity-50 cursor-not-allowed"
                          )}
                          onClick={() => {
                            if (!isOrgMatch) {
                              // For now, store selected columns in a temp merge
                              toggleMergedColumn('__temp__', col);
                            }
                          }}
                        >
                          {col}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Merge into Field</Label>
                  <Select 
                    value=""
                    onValueChange={(targetField) => {
                      if (mergedFields.__temp__?.length > 0) {
                        setMergedFields(prev => {
                          const temp = prev.__temp__ || [];
                          const { __temp__, ...rest } = prev;
                          return { ...rest, [targetField]: temp };
                        });
                      }
                    }}
                    disabled={!mergedFields.__temp__?.length}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={mergedFields.__temp__?.length ? "Select target field..." : "Select columns first"} />
                    </SelectTrigger>
                    <SelectContent>
                      {getTargetFields().map(field => (
                        <SelectItem key={field.key} value={field.key}>
                          {field.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {mergedFields.__temp__?.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Selected: {mergedFields.__temp__.join(' + ')}
                    </p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Mapping Table */}
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-1/3">File Column</TableHead>
                    <TableHead className="w-1/3">Map to Field</TableHead>
                    <TableHead className="w-1/3">Sample Data</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {columnMappings.map((mapping) => {
                    const sampleValue = importSession.sample_rows?.[0]?.[mapping.source_column];
                    const targetFields = getTargetFields();
                    const selectedField = targetFields.find(f => f.key === mapping.target_field);
                    const isReference = selectedField?.type === 'reference' || mapping.target_field === 'org_id';
                    const isOrgMatchCol = mapping.source_column === orgMatchColumn;
                    const isInMerge = Object.values(mergedFields).flat().includes(mapping.source_column);
                    
                    return (
                      <TableRow key={mapping.source_column} className={cn(
                        isOrgMatchCol && "bg-orange-50 dark:bg-orange-950/20",
                        isInMerge && "bg-blue-50 dark:bg-blue-950/20"
                      )}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            {mapping.source_column}
                            {isOrgMatchCol && (
                              <Badge variant="outline" className="text-[10px] border-orange-500 text-orange-600">
                                Org Match
                              </Badge>
                            )}
                            {isInMerge && (
                              <Badge variant="outline" className="text-[10px] border-blue-500 text-blue-600">
                                Merged
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {isOrgMatchCol ? (
                            <div className="text-sm text-orange-600 dark:text-orange-400">
                              → Used for Organisation matching
                            </div>
                          ) : isInMerge ? (
                            <div className="text-sm text-blue-600 dark:text-blue-400">
                              → Merged into another field
                            </div>
                          ) : (
                          <div className="space-y-2">
                            <Select
                              value={mapping.target_field}
                              onValueChange={(value) => updateMapping(mapping.source_column, 'target_field', value)}
                            >
                              <SelectTrigger className="w-full" data-testid={`mapping-${mapping.source_column}`}>
                                <SelectValue placeholder="Select field..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__skip__">-- Skip this column --</SelectItem>
                                {targetFields.map(field => (
                                  <SelectItem key={field.key} value={field.key}>
                                    <div className="flex items-center gap-2">
                                      {field.label}
                                      {field.required && <span className="text-red-500">*</span>}
                                      <Badge variant="secondary" className={cn("text-[10px]", getTypeBadgeColor(field.type))}>
                                        {field.type}
                                      </Badge>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            
                            {/* Reference field - select specific org or match by name */}
                            {isReference && mapping.target_field === 'org_id' && (
                              <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">Organisation Matching</Label>
                                <Select
                                  value={mapping.reference_value || '__match_by_name__'}
                                  onValueChange={(value) => {
                                    updateMapping(mapping.source_column, 'reference_value', value === '__match_by_name__' ? '' : value);
                                    updateMapping(mapping.source_column, 'is_reference', true);
                                  }}
                                >
                                  <SelectTrigger className="w-full text-xs">
                                    <SelectValue placeholder="Match by column value OR select specific org..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="__match_by_name__">Match by column value (org name)</SelectItem>
                                    {importSession.available_organizations?.map(org => (
                                      <SelectItem key={org.org_id} value={org.org_id}>
                                        Apply all to: {org.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          </div>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                          {sampleValue !== undefined && sampleValue !== null 
                            ? String(sampleValue).slice(0, 50) 
                            : <span className="italic">empty</span>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            
            {/* Sample Preview */}
            {importSession.sample_rows?.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm">Sample Data Preview (first 5 rows)</Label>
                <div className="border rounded-lg overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {importSession.columns.map((col, idx) => (
                          <TableHead key={idx} className="text-xs whitespace-nowrap">{col}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {importSession.sample_rows.map((row, rowIdx) => (
                        <TableRow key={rowIdx}>
                          {importSession.columns.map((col, colIdx) => (
                            <TableCell key={colIdx} className="text-xs max-w-[150px] truncate">
                              {row[col] !== undefined && row[col] !== null 
                                ? String(row[col]).slice(0, 30) 
                                : '-'}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
            
            {/* Actions */}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={resetImport}>
                Cancel
              </Button>
              <Button 
                onClick={handleValidate} 
                disabled={validating || columnMappings.every(m => !m.target_field || m.target_field === '__skip__')}
                data-testid="validate-import-btn"
              >
                {validating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Validate Data
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Step 3: Validation & Execute */}
      {currentStep === 3 && validationResult && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  {validationResult.invalid_rows === 0 ? (
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-orange-500" />
                  )}
                  Validation Results
                </CardTitle>
                <CardDescription>
                  Review the validation results before importing
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={resetImport}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="p-4 bg-muted/50 rounded-lg text-center">
                <div className="text-2xl font-bold">{validationResult.total_rows}</div>
                <div className="text-sm text-muted-foreground">Total Rows</div>
              </div>
              <div className="p-4 bg-green-100 dark:bg-green-900/30 rounded-lg text-center">
                <div className="text-2xl font-bold text-green-600">{validationResult.valid_rows}</div>
                <div className="text-sm text-muted-foreground">Valid</div>
              </div>
              <div className="p-4 bg-blue-100 dark:bg-blue-900/30 rounded-lg text-center">
                <div className="text-2xl font-bold text-blue-600">{validationResult.new_records}</div>
                <div className="text-sm text-muted-foreground">New Records</div>
              </div>
              <div className="p-4 bg-orange-100 dark:bg-orange-900/30 rounded-lg text-center">
                <div className="text-2xl font-bold text-orange-600">{validationResult.update_records}</div>
                <div className="text-sm text-muted-foreground">Updates</div>
              </div>
              {validationResult.invalid_rows > 0 && (
                <div className="p-4 bg-red-100 dark:bg-red-900/30 rounded-lg text-center">
                  <div className="text-2xl font-bold text-red-600">{validationResult.invalid_rows}</div>
                  <div className="text-sm text-muted-foreground">Errors</div>
                </div>
              )}
            </div>
            
            {/* Errors */}
            {validationResult.errors?.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm flex items-center gap-2 text-red-500">
                  <XCircle className="w-4 h-4" />
                  Errors ({validationResult.invalid_rows} rows)
                </Label>
                <div className="max-h-40 overflow-y-auto border rounded-lg p-3 bg-red-50 dark:bg-red-900/10">
                  {validationResult.errors.slice(0, 10).map((err, idx) => (
                    <div key={idx} className="text-sm text-red-600 dark:text-red-400">
                      <strong>Row {err.row}:</strong> {err.errors?.join(', ')}
                    </div>
                  ))}
                  {validationResult.errors.length > 10 && (
                    <div className="text-sm text-muted-foreground mt-2">
                      ...and {validationResult.errors.length - 10} more errors
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* New Sectors to be Created */}
            {validationResult.new_sectors?.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm flex items-center gap-2 text-blue-500">
                  <Plus className="w-4 h-4" />
                  New Sectors to be Created ({validationResult.new_sectors.length})
                </Label>
                <div className="border rounded-lg p-3 bg-blue-50 dark:bg-blue-900/10">
                  <div className="flex flex-wrap gap-2">
                    {validationResult.new_sectors.map((sector, idx) => (
                      <Badge key={idx} variant="outline" className="bg-blue-100 dark:bg-blue-900/30">
                        {sector}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    These sectors will be automatically created during import
                  </p>
                </div>
              </div>
            )}
            
            {/* Conflict Mode Selection */}
            <div className="space-y-3 p-4 border rounded-lg">
              <Label className="text-sm font-medium">How should existing records be handled?</Label>
              <RadioGroup value={conflictMode} onValueChange={setConflictMode}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="skip" id="skip" />
                  <Label htmlFor="skip" className="font-normal cursor-pointer">
                    <strong>Skip</strong> - Don't modify existing records, only add new ones
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="replace" id="replace" />
                  <Label htmlFor="replace" className="font-normal cursor-pointer">
                    <strong>Replace</strong> - Overwrite existing records with imported values
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="update_empty" id="update_empty" />
                  <Label htmlFor="update_empty" className="font-normal cursor-pointer">
                    <strong>Update Empty Only</strong> - Only fill in blank fields, keep existing data
                  </Label>
                </div>
              </RadioGroup>
            </div>
            
            {/* Preview Table */}
            {validationResult.preview_rows?.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm">Data Preview (first 20 rows after mapping)</Label>
                <ScrollArea className="h-[200px] border rounded-lg">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Row</TableHead>
                          <TableHead className="text-xs">Status</TableHead>
                          {Object.keys(validationResult.preview_rows[0] || {})
                            .filter(k => !k.startsWith('_'))
                            .map((key, idx) => (
                              <TableHead key={idx} className="text-xs whitespace-nowrap">
                                {formatFieldLabel(key)}
                              </TableHead>
                            ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {validationResult.preview_rows.map((row, rowIdx) => (
                          <TableRow key={rowIdx} className={row._errors?.length > 0 ? 'bg-red-50 dark:bg-red-900/10' : ''}>
                            <TableCell className="text-xs">{row._row_num}</TableCell>
                            <TableCell className="text-xs">
                              {row._errors?.length > 0 ? (
                                <Badge variant="destructive" className="text-[10px]">Error</Badge>
                              ) : row._is_update ? (
                                <Badge variant="outline" className="text-[10px] border-orange-500 text-orange-500">Update</Badge>
                              ) : (
                                <Badge variant="outline" className="text-[10px] border-green-500 text-green-500">New</Badge>
                              )}
                            </TableCell>
                            {Object.entries(row)
                              .filter(([k]) => !k.startsWith('_'))
                              .map(([key, value], idx) => (
                                <TableCell key={idx} className="text-xs max-w-[120px] truncate">
                                  {value !== null && value !== undefined ? String(value).slice(0, 30) : '-'}
                                </TableCell>
                              ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </ScrollArea>
              </div>
            )}
            
            {/* Actions */}
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setCurrentStep(2)}>
                Back to Mapping
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={resetImport}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleExecute}
                  disabled={executing || (validationResult.valid_rows === 0)}
                  className={isBarbiePink ? "bg-pink-500 hover:bg-pink-600" : ""}
                  data-testid="execute-import-btn"
                >
                  {executing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Import {validationResult.valid_rows} Records
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Step 4: Complete */}
      {currentStep === 4 && (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle2 className="w-16 h-16 mx-auto text-green-500 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Import Complete!</h3>
            <p className="text-muted-foreground mb-6">
              Your data has been successfully imported. A snapshot has been created for rollback if needed.
            </p>
            <div className="flex justify-center gap-4">
              <Button variant="outline" onClick={() => { fetchHistory(); setShowHistoryDialog(true); }}>
                <History className="w-4 h-4 mr-2" />
                View History
              </Button>
              <Button onClick={resetImport}>
                Start New Import
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Flush-Year Confirmation Dialog */}
      <Dialog open={flushDialogOpen} onOpenChange={(v) => {
        if (!flushing) {
          setFlushDialogOpen(v);
          if (!v) {
            setPendingImportAfterFlush(false); // cancel chained import too
          }
        }
      }}>
        <DialogContent className="sm:max-w-lg" data-testid="flush-year-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Flush {importYear} data from {selectedTable && importTables[selectedTable]?.label}
            </DialogTitle>
            <DialogDescription>
              This permanently deletes ALL records for the selected year — across every organisation in this table.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {flushPreviewLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading record count...
              </div>
            ) : flushPreview ? (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
                <div className="font-semibold mb-1">About to delete:</div>
                <ul className="space-y-1">
                  <li>• <strong>{flushPreview.record_count}</strong> records in <code>{flushPreview.table}</code></li>
                  <li>• Year: <strong>{flushPreview.year}</strong></li>
                  <li>• Organisations affected: <strong>{flushPreview.org_count}</strong></li>
                </ul>
                {flushPreview.record_count === 0 && (
                  <p className="mt-2 text-xs text-muted-foreground italic">No records exist for this year — flush will be a no-op.</p>
                )}
              </div>
            ) : null}

            {/* Backup choice */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Download a full database backup first?</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={flushBackupChoice === 'yes' ? 'default' : 'outline'}
                  onClick={() => setFlushBackupChoice('yes')}
                  disabled={flushing}
                  data-testid="flush-backup-yes"
                >
                  Yes, download backup
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={flushBackupChoice === 'no' ? 'default' : 'outline'}
                  onClick={() => setFlushBackupChoice('no')}
                  disabled={flushing}
                  data-testid="flush-backup-no"
                >
                  No, skip backup
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Recommended: download a full DB backup XLSX so you can restore if needed.
              </p>
            </div>

            {/* Confirmation token */}
            <div className="space-y-2">
              <Label htmlFor="flush-confirm-input" className="text-sm font-medium">
                To confirm, type <code className="bg-muted px-1 rounded">{importYear}</code> below:
              </Label>
              <Input
                id="flush-confirm-input"
                value={flushConfirmInput}
                onChange={(e) => setFlushConfirmInput(e.target.value)}
                placeholder={String(importYear)}
                disabled={flushing}
                data-testid="flush-confirm-input"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setFlushDialogOpen(false)}
              disabled={flushing}
              data-testid="flush-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={executeFlush}
              disabled={
                flushing
                || !flushPreview
                || flushPreview.record_count === 0
                || flushBackupChoice === null
                || flushConfirmInput !== String(importYear)
              }
              data-testid="flush-confirm-btn"
            >
              {flushing ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-1" />
              )}
              {flushBackupChoice === 'yes' ? 'Backup & Flush' : 'Flush Now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Import History
            </DialogTitle>
            <DialogDescription>
              View past imports and revert if needed
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="flex-1 pr-4">
            {loadingHistory ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : importHistory.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No import history found
              </div>
            ) : (
              <div className="space-y-3">
                {importHistory.map((imp) => (
                  <div 
                    key={imp.import_id} 
                    className={cn(
                      "p-4 border rounded-lg space-y-2",
                      imp.status === 'reverted' && "bg-muted/50 opacity-75"
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileSpreadsheet className="w-4 h-4 text-green-500" />
                        <span className="font-medium">{imp.filename}</span>
                        <Badge variant={imp.status === 'reverted' ? 'secondary' : 'default'}>
                          {imp.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        {imp.status === 'completed' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRevert(imp.import_id)}
                            disabled={reverting === imp.import_id}
                            className="text-orange-500 border-orange-500 hover:bg-orange-50"
                            data-testid={`revert-${imp.import_id}`}
                          >
                            {reverting === imp.import_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <RotateCcw className="w-4 h-4 mr-1" />
                                Revert
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    <div className="text-sm text-muted-foreground grid grid-cols-2 md:grid-cols-4 gap-2">
                      <div><strong>Table:</strong> {imp.table}</div>
                      <div><strong>Rows:</strong> {imp.row_count}</div>
                      <div><strong>By:</strong> {imp.created_by_name}</div>
                      <div><strong>Date:</strong> {new Date(imp.created_at).toLocaleString('en-AU', { timeZone: 'Australia/Sydney', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                    </div>
                    
                    {imp.result && (
                      <div className="flex gap-4 text-xs">
                        <span className="text-green-600">Inserted: {imp.result.inserted}</span>
                        <span className="text-blue-600">Updated: {imp.result.updated}</span>
                        <span className="text-muted-foreground">Skipped: {imp.result.skipped}</span>
                      </div>
                    )}
                    
                    {imp.status === 'reverted' && (
                      <div className="text-xs text-orange-600">
                        Reverted by {imp.reverted_by_name} on {new Date(imp.reverted_at).toLocaleString('en-AU', { timeZone: 'Australia/Sydney', day: '2-digit', month: 'short', year: 'numeric' })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHistoryDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Notes Import Section */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileUp className="w-5 h-5" />
            Import Notes from PDF
          </CardTitle>
          <CardDescription>
            Upload one or more PDFs containing organisation notes. Notes will be added to each organisation's Notes → Others section.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* File Upload */}
          {!notesImportSession ? (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <Input
                  type="file"
                  accept=".pdf,.txt"
                  multiple
                  onChange={handleNotesFileSelect}
                  className="flex-1"
                  data-testid="notes-file-input"
                />
                <Button 
                  onClick={handleNotesUpload}
                  disabled={!(Array.isArray(notesFile) ? notesFile.length : notesFile) || notesUploading}
                  className={isBarbiePink ? "bg-pink-500 hover:bg-pink-600" : ""}
                >
                  {notesUploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload & Parse
                    </>
                  )}
                </Button>
              </div>
              {notesFile && (
                <div className="text-sm text-muted-foreground" data-testid="notes-files-selected">
                  {Array.isArray(notesFile) ? (
                    <>
                      <p className="font-medium mb-1">Selected ({notesFile.length}):</p>
                      <ul className="list-disc list-inside ml-2 space-y-0.5">
                        {notesFile.map((f, i) => (
                          <li key={i} className="text-xs">{f.name} <span className="text-muted-foreground">({(f.size / 1024).toFixed(0)} KB)</span></li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <p>Selected: {notesFile.name}</p>
                  )}
                </div>
              )}
              <div className="p-4 bg-muted/50 rounded-lg text-sm text-muted-foreground">
                <p className="font-medium mb-2">Expected PDF Format:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Organisation name as header (e.g., "Bright Stars Kindergarten")</li>
                  <li>Date/time stamps (e.g., "2/26/2026 1:06 PM")</li>
                  <li>Creator name (e.g., "Creator: Teresa Somerville")</li>
                  <li>Note content below each timestamp</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Parse Results */}
              <div className="p-4 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  <span className="font-medium">File Parsed Successfully</span>
                </div>
                <div className="text-sm text-muted-foreground space-y-1">
                  <p><strong>Total Notes Found:</strong> {notesImportSession.total_notes}</p>
                  <p><strong>Organisations:</strong> {notesImportSession.organizations_found?.join(', ') || 'None'}</p>
                </div>
              </div>
              
              {/* Sample Notes Preview */}
              {notesImportSession.sample_notes?.length > 0 && (
                <div className="space-y-2">
                  <Label>Sample Notes Preview:</Label>
                  <ScrollArea className="h-48 border rounded-lg p-3">
                    {notesImportSession.sample_notes.map((note, idx) => (
                      <div key={idx} className="p-3 border-b last:border-b-0">
                        <div className="flex items-center gap-2 text-sm mb-1">
                          <Badge variant="outline">{note.organization}</Badge>
                          <span className="text-muted-foreground">{note.date || note.timestamp}</span>
                          {note.creator && <span className="text-xs">by {note.creator}</span>}
                        </div>
                        <p className="text-sm">{note.content?.slice(0, 200)}{note.content?.length > 200 ? '...' : ''}</p>
                      </div>
                    ))}
                  </ScrollArea>
                </div>
              )}
              
              {/* Import Results */}
              {notesImportSession.results && (
                <div className="p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="font-medium mb-2">Import Results:</div>
                  <div className="text-sm space-y-1">
                    <p>Imported: {notesImportSession.results.imported}</p>
                    {notesImportSession.results.duplicates > 0 && (
                      <p>Duplicates (already exist): {notesImportSession.results.duplicates}</p>
                    )}
                    {notesImportSession.results.skipped > 0 && (
                      <p>Skipped: {notesImportSession.results.skipped}</p>
                    )}
                    {notesImportSession.results.contacts_created > 0 && (
                      <p>Contacts Created: {notesImportSession.results.contacts_created}</p>
                    )}
                    {notesImportSession.results.matched_via_contact > 0 && (
                      <p data-testid="notes-matched-via-contact">
                        Matched via existing contact email: {notesImportSession.results.matched_via_contact}
                      </p>
                    )}
                    {notesImportSession.results.reattributed > 0 && (
                      <div className="mt-2 p-2 rounded border border-blue-200 bg-blue-50 dark:bg-blue-950/30">
                        <p className="font-medium text-blue-700 dark:text-blue-300" data-testid="notes-reattributed-count">
                          {notesImportSession.results.reattributed} note(s) moved to the correct organisation
                        </p>
                        {notesImportSession.results.reattributed_details?.length > 0 && (
                          <div className="mt-1 max-h-40 overflow-y-auto space-y-1">
                            {notesImportSession.results.reattributed_details.slice(0, 25).map((d, idx) => (
                              <p key={idx} className="ml-2 text-xs text-muted-foreground">
                                <span className="font-medium">{d.from}</span> → <span className="font-medium">{d.to}</span>
                                {d.date ? <span className="ml-1">({d.date})</span> : null}
                              </p>
                            ))}
                            {notesImportSession.results.reattributed_details.length > 25 && (
                              <p className="ml-2 text-xs italic text-muted-foreground">
                                … and {notesImportSession.results.reattributed_details.length - 25} more
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    {notesImportSession.results.orgs_created?.length > 0 && (
                      <div className="mt-2">
                        <p className="font-medium text-blue-700 dark:text-blue-300">New Organisations Created ({notesImportSession.results.orgs_created.length}):</p>
                        {notesImportSession.results.orgs_created.map((org, idx) => (
                          <p key={idx} className="ml-2 text-xs">{org}</p>
                        ))}
                      </div>
                    )}
                    {notesImportSession.results.by_organization && Object.keys(notesImportSession.results.by_organization).length > 0 && (
                      <div className="mt-2">
                        <p className="font-medium">By Organisation:</p>
                        <div className="max-h-40 overflow-y-auto">
                          {Object.entries(notesImportSession.results.by_organization).map(([org, count]) => (
                            <p key={org} className="ml-2 text-xs">{org}: {count} notes</p>
                          ))}
                        </div>
                      </div>
                    )}
                    {notesImportSession.results.skipped_details?.length > 0 && (
                      <div className="mt-2 border-t pt-2">
                        <p className="font-medium text-orange-600">Skipped Records ({notesImportSession.results.skipped_details.length}):</p>
                        <div className="max-h-48 overflow-y-auto space-y-1.5 mt-1">
                          {notesImportSession.results.skipped_details.map((detail, idx) => (
                            <div key={idx} className="ml-2 text-xs p-2 bg-orange-50 dark:bg-orange-950/30 rounded border border-orange-200 dark:border-orange-800">
                              <p className="font-medium text-orange-700 dark:text-orange-300">{detail.reason}</p>
                              {detail.organization && <p>Org: {detail.organization}</p>}
                              {detail.date && <p>Date: {detail.date}</p>}
                              {detail.content && <p className="text-muted-foreground truncate">Content: {detail.content}</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {notesImportSession.results.errors?.length > 0 && (
                      <div className="mt-2 text-red-600">
                        <p className="font-medium">Errors:</p>
                        {notesImportSession.results.errors.map((err, idx) => (
                          <p key={idx} className="ml-2 text-xs">{err.error}</p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Actions */}
              {!notesImportSession.results && (
                <div className="flex items-start gap-3 p-3 rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30">
                  <input
                    type="checkbox"
                    id="notes-force-reattribute"
                    className="mt-1"
                    checked={notesForceReattribute}
                    onChange={(e) => setNotesForceReattribute(e.target.checked)}
                    data-testid="notes-force-reattribute-checkbox"
                  />
                  <label htmlFor="notes-force-reattribute" className="text-sm cursor-pointer">
                    <span className="font-medium">Reattribute existing notes to the correct organisation</span>
                    <span className="block text-xs text-muted-foreground mt-1">
                      Recommended for re-imports. If a note from this PDF was previously imported into the WRONG organisation (e.g. all "Grove Academy" notes ending up in just one of them), this will MOVE it to the correct organisation. Untick if you only want to add new notes.
                    </span>
                  </label>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={resetNotesImport}>
                  Cancel
                </Button>
                {!notesImportSession.results && (
                  <Button 
                    onClick={handleNotesImport}
                    disabled={notesImporting}
                    className={isBarbiePink ? "bg-pink-500 hover:bg-pink-600" : ""}
                    data-testid="execute-notes-import-btn"
                  >
                    {notesImporting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <ArrowRight className="w-4 h-4 mr-2" />
                        Import {notesImportSession.total_notes} Notes
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Live import progress modal — non-dismissable while running */}
      <Dialog open={!!notesProgress} onOpenChange={() => {}}>
        <DialogContent
          className="max-w-md"
          data-testid="notes-import-progress-dialog"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              Importing notes…
            </DialogTitle>
            <DialogDescription>
              Don't close this window. The import runs in the background and is safe to leave open.
            </DialogDescription>
          </DialogHeader>
          {notesProgress && (
            <div className="space-y-3">
              <div className="rounded border bg-muted/40 p-3">
                <p className="text-sm font-medium" data-testid="notes-import-progress-text">
                  {notesProgress.progress || 'Working…'}
                </p>
              </div>
              {notesProgress.running_results && (
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="rounded border p-2">
                    <p className="text-xs text-muted-foreground">Imported</p>
                    <p className="text-lg font-semibold">{notesProgress.running_results.imported ?? 0}</p>
                  </div>
                  <div className="rounded border p-2">
                    <p className="text-xs text-muted-foreground">Duplicates</p>
                    <p className="text-lg font-semibold">{notesProgress.running_results.duplicates ?? 0}</p>
                  </div>
                  <div className="rounded border p-2">
                    <p className="text-xs text-muted-foreground">Skipped</p>
                    <p className="text-lg font-semibold">{notesProgress.running_results.skipped ?? 0}</p>
                  </div>
                  <div className="rounded border p-2">
                    <p className="text-xs text-muted-foreground">Matched via contact</p>
                    <p className="text-lg font-semibold">{notesProgress.running_results.matched_via_contact ?? 0}</p>
                  </div>
                  {(notesProgress.running_results.contacts_created ?? 0) > 0 && (
                    <div className="rounded border p-2 col-span-2">
                      <p className="text-xs text-muted-foreground">Contacts created</p>
                      <p className="text-lg font-semibold">{notesProgress.running_results.contacts_created}</p>
                    </div>
                  )}
                  {(notesProgress.running_results.reattributed ?? 0) > 0 && (
                    <div className="rounded border p-2 col-span-2">
                      <p className="text-xs text-muted-foreground">Reattributed (moved to correct org)</p>
                      <p className="text-lg font-semibold">{notesProgress.running_results.reattributed}</p>
                    </div>
                  )}
                </div>
              )}
              {notesProgress.total_notes ? (
                <p className="text-xs text-muted-foreground text-center">
                  Total notes in this batch: {notesProgress.total_notes}
                </p>
              ) : null}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};



export { ImportSection };
export default ImportSection;
